import GenerateSchemaForm from '@hooks/generateSchemaForm';
import React, { forwardRef, useState, useEffect } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { updateActionItem } from '@reduxLocal/actions/actions';
import { store } from '@reduxLocal/persistState';
import apis from '@api/api';
import { schemaCache } from '@utils/schemaCache';

const actionOptionStyles = StyleSheet.create({
  container: {
    borderRadius: 10, flex: 1,
    minHeight: 200,
  },
})

const actionList = [{
  key: 'email',
  icon: 'mail',
  title: 'Email',
  component: forwardRef(({ index, initialVals }, ref) => {
    const formatInitialVals = () => {
      if (initialVals?.stage_action) {
        return { ...initialVals, stage_action: initialVals.stage_action.title }
      }
      return initialVals;
    }
    return (
      <View style={[actionOptionStyles.container]}>
        <GenerateSchemaForm
          ref={ref}
          avoidKeyboardDisabled
          buttonLabel='Save'
          scrollEnabled
          customStyling={{ minHeight: 400, paddingLeft: 20, paddingTop: 30 }}
          initialData={formatInitialVals()}
          schema={{
        'stage_action': { type: 'template_select', ref: 'template_select' }, 
        'headline': { type:"string" },
        'when': {
          type: 'dropDown',
          content: { enum: ['immediately', 'after', 'before', 'on'] }
        }, 
          }}
          hideButton
          submitFunction={(vals) => {
        return new Promise((resolve) => {
          let newVals;
          if(Object.keys(vals).includes('stage_action')){
            newVals = { ...vals, stage_action: vals.stage_action?._id }
          }
          store.dispatch(updateActionItem(newVals, index));
          resolve(newVals);
        });
          }}
        />
      </View>
    )
  })
}, {
  key: 'project',
  icon: 'folder',
  title: 'Project',
  component: forwardRef(({ index, initialVals }, ref) => {
    const [projectSchema, setProjectSchema] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadSchema = async () => {
        try {
          // Try cache first
          let schema = schemaCache.getSchema('projectSchema');
          if (!schema) {
            // Fetch if not cached
            const res = await apis.getProjectSchema();
            schema = res?.schema || res?.data?.schema || res;
          }
          setProjectSchema(schema);
        } catch (error) {
          console.error('Error loading project schema:', error);
        } finally {
          setLoading(false);
        }
      };
      loadSchema();
    }, []);

    const formatInitialVals = () => {
      if (initialVals?.stage_action && typeof initialVals.stage_action === 'object') {
        return { ...initialVals.stage_action };
      }
      return initialVals || {};
    }

    if (loading || !projectSchema) {
      return (
        <View style={[actionOptionStyles.container, { paddingTop: 30, justifyContent: 'center', alignItems: 'center' }]}>
          <Text>Loading project schema...</Text>
        </View>
      );
    }

    return (
      <View style={[actionOptionStyles.container, { paddingTop: 30 }]}>
        <GenerateSchemaForm
          ref={ref}
          avoidKeyboardDisabled
          buttonLabel='Save'
          scrollEnabled
          customStyling={{ minHeight: 400, paddingLeft: 20, paddingTop: 30 }}
          initialData={formatInitialVals()}
          schema={projectSchema}
          hideButton
          submitFunction={(vals) => {
            return new Promise((resolve) => {
              // Save the entire form data as stage_action (project creation data)
              const newVals = {
                ...initialVals,
                stage_action: vals, // Store the project creation data
                headline: vals.name || vals.title || 'New Project',
                when: vals.when || 'immediately'
              };
              store.dispatch(updateActionItem(newVals, index));
              resolve(newVals);
            });
          }}
        />
      </View>
    )
  })
}, 
{
  key: 'calendar',
  icon: 'calendar',
  title: 'Calendar',
  component: forwardRef(({ index, initialVals }, ref) => {
    const [calendarSchema, setCalendarSchema] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadSchema = async () => {
        try {
          // Try cache first
          let schema = schemaCache.getSchema('calendarEntrySchema');
          if (!schema) {
            // Fetch if not cached
            const res = await apis.getCalendarSchema();
            schema = res?.schema || res?.data?.schema || res;
          }
          setCalendarSchema(schema);
        } catch (error) {
          console.error('Error loading calendar schema:', error);
        } finally {
          setLoading(false);
        }
      };
      loadSchema();
    }, []);

    const formatInitialVals = () => {
      if (initialVals?.stage_action && typeof initialVals.stage_action === 'object') {
        return { ...initialVals.stage_action };
      }
      return initialVals || {};
    }

    if (loading || !calendarSchema) {
      return (
        <View style={[actionOptionStyles.container, { paddingTop: 30, justifyContent: 'center', alignItems: 'center' }]}>
          <Text>Loading calendar schema...</Text>
        </View>
      );
    }

    return (
      <View style={[actionOptionStyles.container, { paddingTop: 30 }]}>
        <GenerateSchemaForm
          ref={ref}
          avoidKeyboardDisabled
          buttonLabel='Save'
          scrollEnabled
          customStyling={{ minHeight: 400, paddingLeft: 20, paddingTop: 30 }}
          initialData={formatInitialVals()}
          schema={calendarSchema}
          hideButton
          submitFunction={(vals) => {
            return new Promise((resolve) => {
              // Save the entire form data as stage_action (calendar event creation data)
              const newVals = {
                ...initialVals,
                stage_action: vals, // Store the calendar event creation data
                headline: vals.title || vals.name || 'New Calendar Event',
                when: vals.when || 'immediately'
              };
              store.dispatch(updateActionItem(newVals, index));
              resolve(newVals);
            });
          }}
        />
      </View>
    )
  })
  }, {
    key: 'document',
    icon: 'file',
    title: 'Document',
    component: forwardRef(({ index, initialVals }, ref) => {
      const formatInitialVals = () => {
        if (initialVals?.stage_action && typeof initialVals.stage_action === 'object') {
          return { ...initialVals.stage_action };
        }
        return initialVals || {};
      }

      // Document generation schema - basic fields for creating a document
      const documentSchema = {
        'templateId': {
          type: 'template_select',
          ref: 'template_select',
          content: { label: 'Document Template' }
        },
        'name': {
          type: 'string',
          content: { label: 'Document Name', placeholder: 'Enter document name' }
        },
        'description': {
          type: 'string',
          content: { label: 'Description', placeholder: 'Enter document description' }
        },
        'when': {
          type: 'dropDown',
          content: { enum: ['immediately', 'after', 'before', 'on'], label: 'When to generate' }
        },
      };

      return (
        <View style={[actionOptionStyles.container, { paddingTop: 30 }]}>
          <GenerateSchemaForm
            ref={ref}
            avoidKeyboardDisabled
            buttonLabel='Save'
            scrollEnabled
            customStyling={{ minHeight: 400, paddingLeft: 20, paddingTop: 30 }}
            initialData={formatInitialVals()}
            schema={documentSchema}
            hideButton
          submitFunction={(vals) => {
            return new Promise((resolve) => {
              // Save the entire form data as stage_action (document generation data)
              const newVals = {
                ...initialVals,
                stage_action: vals, // Store the document creation data
                headline: vals.name || 'New Document',
                when: vals.when || 'immediately'
              };
              store.dispatch(updateActionItem(newVals, index));
              resolve(newVals);
            });
          }}
        />
      </View>
    )
  })
  }, {
  key: 'integration',
  icon: 'link',
  title: 'Integration',
  component: forwardRef(({ index, initialVals }, ref) => {
    const formatInitialVals = () => {
      if (initialVals?.stage_action && typeof initialVals.stage_action === 'object') {
        return { ...initialVals.stage_action };
      }
      return initialVals || {};
    }

    // Integration setup schema - basic fields for configuring an integration
    const integrationSchema = {
      'integrationType': {
        type: 'dropDown',
        content: {
          enum: ['email', 'calendar', 'whatsapp', 'slack', 'zapier', 'webhook', 'api'],
          label: 'Integration Type'
        }
      },
      'name': {
        type: 'string',
        content: { label: 'Integration Name', placeholder: 'Enter integration name' }
      },
      'config': {
        type: 'object',
        content: { label: 'Configuration', placeholder: 'Integration configuration' }
      },
      'when': {
        type: 'dropDown',
        content: { enum: ['immediately', 'after', 'before', 'on'], label: 'When to trigger' }
      },
    };

    return (
      <View style={[actionOptionStyles.container, { paddingTop: 30 }]}>
        <GenerateSchemaForm
          ref={ref}
          avoidKeyboardDisabled
          buttonLabel='Save'
          scrollEnabled
          customStyling={{ minHeight: 400, paddingLeft: 20, paddingTop: 30 }}
          initialData={formatInitialVals()}
          schema={integrationSchema}
          hideButton
          submitFunction={(vals) => {
            return new Promise((resolve) => {
              // Save the entire form data as stage_action (integration setup data)
              const newVals = {
                ...initialVals,
                stage_action: vals, // Store the integration setup data
                headline: vals.name || 'New Integration',
                when: vals.when || 'immediately'
              };
              store.dispatch(updateActionItem(newVals, index));
              resolve(newVals);
            });
          }}
        />
      </View>
    )
  })
  }, {
    key: 'call',
    icon: 'phone',
  title: 'Call',
  component: forwardRef(({ index, initialVals }, ref) => {
    const formatInitialVals = () => {
      if (initialVals?.stage_action && typeof initialVals.stage_action === 'object') {
        return { ...initialVals.stage_action };
      }
      return initialVals || {};
    }

    // Call configuration schema
    const callSchema = {
      'phoneNumber': {
        type: 'string',
        content: { label: 'Phone Number', placeholder: '+1234567890' }
      },
      'callType': {
        type: 'dropDown',
        content: {
          enum: ['voice', 'video', 'conference'],
          label: 'Call Type'
        }
      },
      'duration': {
        type: 'number',
        content: { label: 'Duration (minutes)', placeholder: '30' }
      },
      'when': {
        type: 'dropDown',
        content: { enum: ['immediately', 'after', 'before', 'on'], label: 'When to call' }
      },
    };

    return (
      <View style={[actionOptionStyles.container, { paddingTop: 30 }]}>
        <GenerateSchemaForm
          ref={ref}
          avoidKeyboardDisabled
          buttonLabel='Save'
          scrollEnabled
          customStyling={{ minHeight: 400, paddingLeft: 20, paddingTop: 30 }}
          initialData={formatInitialVals()}
          schema={callSchema}
          hideButton
          submitFunction={(vals) => {
            return new Promise((resolve) => {
              // Save the entire form data as stage_action (call configuration data)
              const newVals = {
                ...initialVals,
                stage_action: vals, // Store the call configuration data
                headline: `Call ${vals.phoneNumber || 'Number'}`,
                when: vals.when || 'immediately'
              };
              store.dispatch(updateActionItem(newVals, index));
              resolve(newVals);
            });
          }}
        />
      </View>
    )
  })
  },
  {
    key: 'whatsapp',
    icon: 'whatsapp',
    title: 'WhatsApp',
    component: forwardRef(({ index, initialVals }, ref) => {
      const formatInitialVals = () => {
        if (initialVals?.stage_action && typeof initialVals.stage_action === 'object') {
          return { ...initialVals.stage_action };
        }
        return initialVals || {};
      }

      // WhatsApp message creation schema
      const whatsappSchema = {
        'to': {
          type: 'string',
          content: { label: 'Phone Number', placeholder: '+1234567890' }
        },
        'message': {
          type: 'string',
          content: { label: 'Message', placeholder: 'Enter WhatsApp message' }
        },
        'templateName': {
          type: 'string',
          content: { label: 'Template Name (optional)', placeholder: 'Template name if using template' }
        },
        'when': {
          type: 'dropDown',
          content: { enum: ['immediately', 'after', 'before', 'on'], label: 'When to send' }
        },
      };

      return (
        <View style={[actionOptionStyles.container, { paddingTop: 30 }]}>
          <GenerateSchemaForm
            ref={ref}
            avoidKeyboardDisabled
            buttonLabel='Save'
            scrollEnabled
            customStyling={{ minHeight: 400, paddingLeft: 20, paddingTop: 30 }}
            initialData={formatInitialVals()}
            schema={whatsappSchema}
            hideButton
            submitFunction={(vals) => {
              return new Promise((resolve) => {
                // Save the entire form data as stage_action (WhatsApp message data)
                const newVals = {
                  ...initialVals,
                  stage_action: vals, // Store the WhatsApp message creation data
                  headline: vals.message?.substring(0, 30) || 'WhatsApp Message',
                  when: vals.when || 'immediately'
                };
                store.dispatch(updateActionItem(newVals, index));
                resolve(newVals);
              });
            }}
          />
        </View>
      )
    })
  }
];

export default actionList;